import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Text, Box, Flex, Button, Loader } from '@vibe/core'
import { Dropdown } from '@vibe/core/next'
import { apiService } from '../../services/api'
import { getErrorMessage } from '../../utils/errorHandling'
import type { JobDefinitionSelectorOption } from '../../types'

interface JobDefinitionSelectorProps {
  value?: string
  onChange: (
    jobDefinitionId: string,
    jobDefinition?: JobDefinitionSelectorOption
  ) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  error?: string
  className?: string
}

interface SelectorState {
  options: JobDefinitionSelectorOption[]
  loading: boolean
  error: string | null
}

export const JobDefinitionSelector: React.FC<JobDefinitionSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Seleccionar definición de puesto...',
  required = false,
  disabled = false,
  error,
  className,
}) => {
  const [state, setState] = useState<SelectorState>({
    options: [],
    loading: true,
    error: null,
  })

  // Load job definition options
  const loadOptions = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))

      const options = await apiService.getJobDefinitionOptions()

      setState(prev => ({
        ...prev,
        options,
        loading: false,
      }))
    } catch (error) {
      console.error('Error loading job definition options:', error)
      setState(prev => ({
        ...prev,
        loading: false,
        error: getErrorMessage(error),
      }))
    }
  }, [])

  // Initial load
  useEffect(() => {
    loadOptions()
  }, [loadOptions])

  // Convert to dropdown format
  const dropdownOptions = useMemo(() => {
    return state.options.map(option => ({
      value: option.value,
      label: option.label,
      description: option.puesto,
    }))
  }, [state.options])

  // Get selected option
  const selectedOption = useMemo(() => {
    if (!value) return undefined
    return dropdownOptions.find(option => option.value === value) || undefined
  }, [value, dropdownOptions])

  // Handle selection change
  const handleChange = useCallback(
    (selectedOption: any) => {
      const jobDefinitionId = selectedOption?.value || ''
      const jobDefinition = state.options.find(
        opt => opt.value === jobDefinitionId
      )
      onChange(jobDefinitionId, jobDefinition)
    },
    [onChange, state.options]
  )

  // Refresh options
  const handleRefresh = useCallback(() => {
    loadOptions()
  }, [loadOptions])

  if (state.loading && state.options.length === 0) {
    return (
      <Box className={className}>
        <Flex
          align="center"
          gap="small"
          className="p-3 border border-gray-200 rounded-lg"
        >
          <Loader size="small" />
          <Text className="text-sm text-gray-600">
            Cargando definiciones...
          </Text>
        </Flex>
      </Box>
    )
  }

  if (state.error) {
    return (
      <Box className={className}>
        <Box className="p-3 border border-red-200 bg-red-50 rounded-lg">
          <Text className="text-sm text-red-700 mb-2">{state.error}</Text>
          <Button kind="tertiary" size="small" onClick={handleRefresh}>
            Reintentar
          </Button>
        </Box>
      </Box>
    )
  }

  if (state.options.length === 0) {
    return (
      <Box className={className}>
        <Box className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <Text className="text-sm text-gray-700 mb-3">
            No hay definiciones de puesto disponibles.
          </Text>
          <Button kind="tertiary" size="small" onClick={handleRefresh}>
            Actualizar
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box className={className}>
      <Dropdown
        placeholder={placeholder}
        options={dropdownOptions}
        value={selectedOption}
        onChange={handleChange}
        error={!!error}
        helperText={error}
        required={required}
        disabled={disabled}
        ariaLabel="Seleccionar definición de puesto"
        clearable={false}
      />
    </Box>
  )
}

export default JobDefinitionSelector
